/**
 * Surety bond request enclosures — pure helpers.
 *
 * Builds the two generated enclosures that ride behind the request letter:
 *   A. Project schedule — one bar per phase (Design → Procurement → Production → Installation)
 *   B. Contract summary — signed base, approved change orders, and the 50/40/10 installments
 * No firebase imports; the wizard supplies the data.
 */

import type { ChangeOrder } from '../types';
import type {
  SuretyContractSummaryEnclosure,
  SuretyContractSummaryInstallment,
  SuretyEnclosureDocs,
  SuretySchedulePhase,
  SuretySchedulePhaseKey,
} from '../types/regulatory';
import { buildPaymentScheduleBreakdown, type PaymentInstallmentId } from './paymentSchedule';
import {
  DEFAULT_PAYMENT_LAG_SETTINGS,
  addBusinessDays,
  installmentLagBusinessDays,
  taskEffectiveEndDate,
  type PaymentLagSettings,
  type TaskGroupLike,
} from './paymentDueFlags';
import { SIMULATED_UC_GROUP_DEFS } from './simulateUnderContract';
import { parseYmdLocal, ymdLocal } from './suretyBondRequest';

/** Fixed phase order for the schedule enclosure. */
export const SURETY_SCHEDULE_PHASES: ReadonlyArray<{ key: SuretySchedulePhaseKey; name: string; color: string; share: number }> =
  SIMULATED_UC_GROUP_DEFS.map((g) => ({
    key: g.key as SuretySchedulePhaseKey,
    name: g.name,
    color: g.color,
    // Design 30% · Procurement 20% · Production 30% · Installation 20% of the window.
    share: g.key === 'design' || g.key === 'production' ? 0.3 : 0.2,
  }));

export const SURETY_ENCLOSURE_SCHEDULE_LABEL = 'Enclosure A — Project schedule';
export const SURETY_ENCLOSURE_CONTRACT_LABEL = 'Enclosure B — Contract summary';
const GENERATED_PREFIX_RE = /^Enclosure [A-Z] — /;

/** Same rule as contractValue.isApprovedChangeOrder, kept local so this module stays firebase-free. */
function isApprovedChangeOrder(co: Pick<ChangeOrder, 'status'>): boolean {
  return co.status === 'approved';
}

export function phaseColor(key: string): string {
  return SURETY_SCHEDULE_PHASES.find((p) => p.key === key)?.color ?? '#64748b';
}

type DateLike = Date | { toDate?: () => Date } | string | number | null | undefined;

function toDate(value: DateLike): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, days: number): Date {
  const n = new Date(d);
  n.setDate(n.getDate() + days);
  return n;
}

/** Which schedule phase a Hub task group belongs to, by name (same includes-match the planner uses). */
export function phaseKeyForGroupName(name: string): SuretySchedulePhaseKey | null {
  const n = (name || '').trim().toLowerCase();
  if (!n) return null;
  if (n.includes('design') || n.includes('engineering') || n.includes('drawing')) return 'design';
  if (n.includes('procure') || n.includes('purchas') || n.includes('material')) return 'procurement';
  if (n.includes('production') || n.includes('shop') || n.includes('fabricat') || n.includes('cut')) return 'production';
  if (n.includes('install') || n.includes('delivery') || n.includes('raising') || n.includes('site')) return 'installation';
  return null;
}

export interface SchedulePhaseTaskLike {
  id?: string;
  projectId: string;
  groupId?: string;
  parentTaskId?: string;
  title?: string;
  startDate?: DateLike;
  endDate: DateLike;
  actualEndDate?: DateLike;
  status?: string;
}

/**
 * Phase bars from the project's Hub tasks: each phase spans the earliest start
 * to the latest effective end of tasks in groups that map to that phase.
 * Phases with no tasks are omitted. Empty when the project has no dated tasks.
 */
export function phaseScheduleFromTasks(
  projectId: string,
  tasks: SchedulePhaseTaskLike[],
  groups: TaskGroupLike[],
): SuretySchedulePhase[] {
  const groupPhase = new Map<string, SuretySchedulePhaseKey>();
  for (const g of groups) {
    if (g.projectId !== projectId || !g.id) continue;
    const key = phaseKeyForGroupName(g.name);
    if (key) groupPhase.set(g.id, key);
  }
  const spans = new Map<SuretySchedulePhaseKey, { start: Date; end: Date }>();
  for (const t of tasks) {
    if (t.projectId !== projectId || !t.groupId) continue;
    const key = groupPhase.get(t.groupId);
    if (!key) continue;
    const end = taskEffectiveEndDate(t as Parameters<typeof taskEffectiveEndDate>[0]);
    const start = toDate(t.startDate) ?? end;
    if (!end || !start) continue;
    const span = spans.get(key);
    if (!span) spans.set(key, { start: startOfDay(start), end: startOfDay(end) });
    else {
      if (start < span.start) span.start = startOfDay(start);
      if (end > span.end) span.end = startOfDay(end);
    }
  }
  return SURETY_SCHEDULE_PHASES
    .filter((p) => spans.has(p.key))
    .map((p) => {
      const span = spans.get(p.key)!;
      return {
        key: p.key,
        name: p.name,
        startDate: ymdLocal(span.start),
        endDate: ymdLocal(span.end > span.start ? span.end : span.start),
        source: 'tasks' as const,
      };
    });
}

/**
 * Split the start → completion window into the four phases (30/20/30/20),
 * back-to-back, each at least one day. Used when the Hub has no schedule yet —
 * the same idea as "simulate under contract" on the staff planner.
 */
export function simulatePhaseSchedule(start: string | Date, completion: string | Date): SuretySchedulePhase[] {
  const s0 = typeof start === 'string' ? parseYmdLocal(start) : startOfDay(start);
  const e0 = typeof completion === 'string' ? parseYmdLocal(completion) : startOfDay(completion);
  if (!s0) return [];
  const minEnd = addDays(s0, SURETY_SCHEDULE_PHASES.length - 1);
  const e = e0 && e0 > minEnd ? e0 : minEnd;
  const totalDays = Math.round((e.getTime() - s0.getTime()) / 86_400_000);
  const phases: SuretySchedulePhase[] = [];
  let cursor = 0;
  SURETY_SCHEDULE_PHASES.forEach((p, i) => {
    const isLast = i === SURETY_SCHEDULE_PHASES.length - 1;
    const len = isLast ? Math.max(1, totalDays - cursor) : Math.max(1, Math.round(totalDays * p.share));
    const phaseStart = addDays(s0, cursor);
    const phaseEnd = isLast ? e : addDays(phaseStart, Math.max(0, len - 1));
    phases.push({
      key: p.key,
      name: p.name,
      startDate: ymdLocal(phaseStart),
      endDate: ymdLocal(phaseEnd < phaseStart ? phaseStart : phaseEnd),
      source: 'simulated',
    });
    cursor += len;
  });
  return phases;
}

export interface ScheduleChartBar {
  key: string;
  name: string;
  color: string;
  /** 0–1 fractions of the chart width. */
  left: number;
  width: number;
  startDate: string;
  endDate: string;
  days: number;
}

export interface ScheduleChartTick {
  /** 0–1 fraction. */
  x: number;
  label: string;
}

export interface ScheduleChartLayout {
  rangeStart: string;
  rangeEnd: string;
  totalDays: number;
  bars: ScheduleChartBar[];
  /** First-of-month ticks; every Nth month when the window is long. */
  ticks: ScheduleChartTick[];
  /** Fraction for the today marker, or null when outside the window. */
  today: number | null;
}

/** Fractions for drawing the phase bars + month ticks. Pure so the PDF and the wizard preview agree. */
export function scheduleChartLayout(phases: SuretySchedulePhase[], today: Date = new Date()): ScheduleChartLayout | null {
  const parsed = phases
    .map((p) => ({ p, s: parseYmdLocal(p.startDate), e: parseYmdLocal(p.endDate) }))
    .filter((x): x is { p: SuretySchedulePhase; s: Date; e: Date } => !!x.s && !!x.e);
  if (parsed.length === 0) return null;
  let min = parsed[0].s;
  let max = parsed[0].e;
  for (const x of parsed) {
    if (x.s < min) min = x.s;
    if (x.e > max) max = x.e;
  }
  // Pad to whole-month boundaries so ticks sit at the edges.
  const rangeStart = new Date(min.getFullYear(), min.getMonth(), 1);
  const rangeEnd = new Date(max.getFullYear(), max.getMonth() + 1, 0);
  const totalDays = Math.max(1, Math.round((rangeEnd.getTime() - rangeStart.getTime()) / 86_400_000) + 1);
  const frac = (d: Date) => Math.min(1, Math.max(0, (d.getTime() - rangeStart.getTime()) / 86_400_000 / totalDays));

  const bars: ScheduleChartBar[] = parsed.map(({ p, s, e }) => {
    const left = frac(s);
    const right = frac(addDays(e, 1));
    return {
      key: p.key,
      name: p.name,
      color: phaseColor(p.key),
      left,
      width: Math.max(0.004, right - left),
      startDate: p.startDate,
      endDate: p.endDate,
      days: Math.round((e.getTime() - s.getTime()) / 86_400_000) + 1,
    };
  });

  const months = (rangeEnd.getFullYear() - rangeStart.getFullYear()) * 12 + (rangeEnd.getMonth() - rangeStart.getMonth()) + 1;
  const every = months > 18 ? 3 : months > 9 ? 2 : 1;
  const ticks: ScheduleChartTick[] = [];
  for (let i = 0; i < months; i += every) {
    const d = new Date(rangeStart.getFullYear(), rangeStart.getMonth() + i, 1);
    const showYear = i === 0 || d.getMonth() === 0;
    ticks.push({
      x: frac(d),
      label: d.toLocaleDateString('en-US', showYear ? { month: 'short', year: '2-digit' } : { month: 'short' }),
    });
  }

  const t = startOfDay(today);
  const todayFrac = t >= rangeStart && t <= rangeEnd ? frac(t) : null;

  return { rangeStart: ymdLocal(rangeStart), rangeEnd: ymdLocal(rangeEnd), totalDays, bars, ticks, today: todayFrac };
}

export function totalScheduleDays(phases: SuretySchedulePhase[]): number {
  const layout = scheduleChartLayout(phases);
  if (!layout) return 0;
  const starts = phases.map((p) => parseYmdLocal(p.startDate)).filter((d): d is Date => !!d);
  const ends = phases.map((p) => parseYmdLocal(p.endDate)).filter((d): d is Date => !!d);
  if (!starts.length || !ends.length) return 0;
  const min = new Date(Math.min(...starts.map((d) => d.getTime())));
  const max = new Date(Math.max(...ends.map((d) => d.getTime())));
  return Math.round((max.getTime() - min.getTime()) / 86_400_000) + 1;
}

/** Last phase end — offered back to the request as Substantial completion. */
export function scheduleCompletionDate(phases: SuretySchedulePhase[]): string | null {
  const ends = phases.map((p) => parseYmdLocal(p.endDate)).filter((d): d is Date => !!d);
  if (!ends.length) return null;
  return ymdLocal(new Date(Math.max(...ends.map((d) => d.getTime()))));
}

export function scheduleStartDate(phases: SuretySchedulePhase[]): string | null {
  const starts = phases.map((p) => parseYmdLocal(p.startDate)).filter((d): d is Date => !!d);
  if (!starts.length) return null;
  return ymdLocal(new Date(Math.min(...starts.map((d) => d.getTime()))));
}

export interface BuildContractSummaryInput {
  /** Contract amount on the request — fallback base when no signed proposal total exists. */
  contractAmount: number;
  /** Signed proposal list total (pre-CO), or null when the deal is not yet signed. */
  signedBase: number | null;
  changeOrders: ChangeOrder[];
  phases: SuretySchedulePhase[];
  /** YYYY-MM-DD; the deposit anchors here. */
  contractDate: string | null;
  lag?: PaymentLagSettings;
  today?: Date;
}

function anchorForInstallment(
  id: PaymentInstallmentId,
  phases: SuretySchedulePhase[],
  contractDate: string | null,
): { date: Date | null; basis: string; estimated: boolean } {
  if (id === 'deposit') {
    return { date: parseYmdLocal(contractDate), basis: 'On contract signing', estimated: !contractDate };
  }
  const phaseKey = id === 'pre_shipment' ? 'production' : 'installation';
  const phase = phases.find((p) => p.key === phaseKey);
  const date = parseYmdLocal(phase?.endDate ?? null);
  const basis = id === 'pre_shipment' ? 'Prior to shipment (end of Production)' : 'At completed installation';
  return { date, basis, estimated: !phase || phase.source !== 'tasks' };
}

/**
 * Contract summary enclosure: base, approved change orders, and the 50/40/10
 * installments with due dates anchored to the schedule phases plus the
 * configured payment lag.
 */
export function buildContractSummary(input: BuildContractSummaryInput): SuretyContractSummaryEnclosure {
  const lag = input.lag ?? DEFAULT_PAYMENT_LAG_SETTINGS;
  const today = input.today ?? new Date();
  const base = input.signedBase != null && input.signedBase > 0 ? input.signedBase : Math.max(0, input.contractAmount);
  const approved = input.changeOrders.filter(isApprovedChangeOrder);
  const breakdown = buildPaymentScheduleBreakdown(base, approved, { passedInstallmentIds: [] });

  const changeOrders = approved
    .slice()
    .sort((a, b) => a.number - b.number)
    .map((co) => {
      const approvedAt = toDate(co.approvedAt ?? co.signature?.signedAt ?? null);
      return {
        number: co.number,
        title: co.title || `Change order ${co.number}`,
        amount: Number(co.deltaAmount) || 0,
        approvedAt: approvedAt ? ymdLocal(approvedAt) : null,
      };
    });

  const installments: SuretyContractSummaryInstallment[] = breakdown.rows.map((row) => {
    if (row.kind === 'installment') {
      const id = row.id as PaymentInstallmentId;
      const anchor = anchorForInstallment(id, input.phases, input.contractDate);
      const due = anchor.date ? addBusinessDays(anchor.date, installmentLagBusinessDays(id, lag)) : null;
      const lagDays = installmentLagBusinessDays(id, lag);
      return {
        id,
        label: `${row.label} ${id === 'deposit' ? 'Deposit' : id === 'pre_shipment' ? 'Pre-shipment' : 'Final'}`,
        percent: Math.round((row.percent ?? 0) * 100),
        amount: row.amount,
        dueDate: due ? ymdLocal(due) : null,
        estimated: anchor.estimated,
        basis: lagDays > 0 ? `${anchor.basis} + ${lagDays} business days` : anchor.basis,
      };
    }
    return {
      id: row.id,
      label: row.label,
      percent: 0,
      amount: row.amount,
      dueDate: null,
      estimated: false,
      basis: 'Billed on approval',
    };
  });

  return {
    signedBase: input.signedBase,
    currentContractValue: breakdown.currentContractValue,
    changeOrders,
    installments,
    builtAt: ymdLocal(today),
  };
}

/**
 * Enclosure lines for the letter: generated ones first (A, B), then anything
 * typed by hand. Custom rows that duplicate a generated label are dropped.
 */
export function enclosureLabels(docs: SuretyEnclosureDocs | null | undefined, custom: string[]): string[] {
  const out: string[] = [];
  if (docs?.schedule) out.push(SURETY_ENCLOSURE_SCHEDULE_LABEL);
  if (docs?.contractSummary) out.push(SURETY_ENCLOSURE_CONTRACT_LABEL);
  for (const raw of custom) {
    const s = raw.trim();
    if (!s || GENERATED_PREFIX_RE.test(s)) continue;
    if (out.some((o) => o.toLowerCase() === s.toLowerCase())) continue;
    // A plain "Project schedule" default is superseded by the generated enclosure.
    if (docs?.schedule && s.toLowerCase() === 'project schedule') continue;
    if (docs?.contractSummary && s.toLowerCase() === 'contract summary') continue;
    out.push(s);
  }
  return out;
}

/** True for the labels the wizard writes (not editable by hand). */
export function isGeneratedEnclosureLabel(label: string): boolean {
  return GENERATED_PREFIX_RE.test(label.trim());
}

/** True when the enclosure list names a project schedule (default row or generated label). */
export function wantsScheduleEnclosure(enclosures: readonly string[]): boolean {
  return enclosures.some((s) => /project\s+schedule/i.test(s));
}

/** True when the enclosure list names a contract summary. */
export function wantsContractSummaryEnclosure(enclosures: readonly string[]): boolean {
  return enclosures.some((s) => /contract\s+summary/i.test(s));
}

/**
 * A request that lists the project schedule as an enclosure but has never
 * built it — the PDF would print "Enclosures: Project schedule" with no page
 * behind it, so the download flow opens the wizard first.
 */
export function needsScheduleBeforeDownload(
  request: { enclosures: readonly string[]; enclosureDocs?: SuretyEnclosureDocs | null },
): boolean {
  return wantsScheduleEnclosure(request.enclosures) && !request.enclosureDocs?.schedule?.phases?.length;
}

/**
 * Apply the wizard's result to the request's enclosure list: replace any prior
 * generated rows, keep custom rows, and drop the bare "Project schedule"
 * default once the real schedule exists.
 */
export function mergeEnclosureSelections(existing: string[], generated: SuretyEnclosureDocs | null | undefined): string[] {
  const custom = existing.filter((s) => !isGeneratedEnclosureLabel(s));
  return enclosureLabels(generated, custom);
}
